import Markdown, { Components } from "react-markdown";
import { memo, useContext, useEffect, useMemo } from "react";

import { Box } from "@mui/material";
import { ChatMessageWithParts } from "../types";
import { makeCitationComponents } from "./citations/citationComponents";
import { CodeComponent } from "src/reusable/Chat/AssistantMessage/AssistantText";
import type { Pluggable } from "unified";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import { rehypeWrapCite } from "src/views/assistant/rehype/rehypeWrapCite";
import { rehypeWrapStorageKey } from "src/views/assistant/rehype/rehypeWrapStorageKey";
import { protectCitationMarkup } from "src/views/assistant/rehype/protectCitationMarkup";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import { useArtifactStore } from "src/reusable/Chat/context/ArtifactStore";
import { useCitationSources } from "../hooks/useCitationSources";
import { useUIStore } from "../stores/UIStore";
import { useChatStore } from "../stores/ChatStore";
import {
  useDialogueProvenance,
  useProvenanceDialogueId,
} from "../services/ProvenanceService";
import { mergeCitedWithUnavailable } from "./sources/citedTierMerge";
import {
  citedExternalIdsInOrder,
  collectWebExternalsFromMessages,
} from "./sources/clientWebExternals";
import { useLiveWebExternals } from "./sources/useLiveWebExternals";
import ChatContext from "src/reusable/Chat/context/ChatContext";
import ResponseSourcesSummary from "./sources/ResponseSourcesSummary";
import SubAgentContributionStrip from "./SubAgentContributionStrip";

// Inline sub-agent section banners (#20423 WS6 level b) were removed pending a
// placement rework — the paragraph-index placement could attach a banner to
// the WRONG block (silent mis-attribution in a compliance feature). See git
// history (SubAgentSectionBanner / rehypeSubAgentBanners /
// computeSubAgentSections) for the scaffolding; a rework needs offset-based
// placement instead. The SubAgentContributionStrip (level a, message-scope
// attribution) is unaffected and stays on.

export type AAAssistantTextProps = {
  chatMessage: ChatMessageWithParts;
  content: string;
  responseSources?: any[];
  onSourcesUpdate?: (sources: any[]) => void;
};

const AAAssistantText: React.FC<AAAssistantTextProps> = memo(
  ({ chatMessage, content, responseSources = [], onSourcesUpdate }) => {
    // Get artifact store functions
    const addArtifact = useArtifactStore((state) => state.addArtifact);

    // Current dialogue id, used to scope both the provenance fetch below and
    // the by-ids citation fetch (#1900 — dialogue citation allowlist).
    const dialogueId = useProvenanceDialogueId();

    // Batch fetch missing citation sources for old conversations
    const {
      chunkMap,
      isLoading: citationsLoading,
      fetchedChunks,
    } = useCitationSources(content, dialogueId);

    // Merge fetched chunks into response sources for the sources bar
    const mergedResponseSources = useMemo(() => {
      if (!fetchedChunks || fetchedChunks.length === 0) {
        return responseSources;
      }

      // Create a Set of existing chunk IDs to avoid duplicates
      const existingChunkIds = new Set(
        responseSources.map((source) => source.data_chunk?.id).filter(Boolean),
      );

      // The API returns SearchResult format, same as SSE sources
      // Just filter duplicates - no transformation needed
      const newSources = fetchedChunks
        .filter((chunk) => !existingChunkIds.has(chunk.data_chunk?.id))
        .map((chunk) => ({
          ...chunk,
          // Ensure dataset_type exists with a default structure for display
          dataset: {
            ...chunk.dataset,
            dataset_type: chunk.dataset?.dataset_type || { name: "Document" },
          },
        }));

      // Return combined sources
      return [...responseSources, ...newSources];
    }, [responseSources, fetchedChunks]);

    // Dialogue-global citation numbering (#20423 Story 1/3): inline [N] markers
    // are numbered by a cited chunk's first-seen position across the WHOLE
    // session, not per-message — with the numbers built through
    // mergeCitedWithUnavailable so a cited-but-unavailable document keeps its
    // slot (gap-stable [N], agreeing with the Sources panel and pills).
    // Per-message chunks not yet in the dialogue payload (the turn still
    // streaming/finalizing) are appended with provisional numbers after the
    // last occupied slot until the next refetch folds them into the global
    // order.
    const { data: dialogueProvenance } = useDialogueProvenance(dialogueId);

    // Web-search results are already in the browser the instant the user
    // approves (WebSearchApproval stitches them onto the tool_response part), so
    // inline [N] numbering can resolve LIVE from the conversation itself — no
    // provenance fetch, no poll, no reload (#20423 A2).
    //
    // The LIVE turn's dialogue lives in the per-dialogue STREAMING store
    // (StreamManagerStore), not the assistant ChatStore — ChatStore.dialogue is
    // only populated when a saved dialogue is loaded. useLiveWebExternals
    // subscribes to the same stream WebSearchApproval writes, resolved the way
    // it resolves it (dialogueId from ChatContext).
    const chatCtx = useContext(ChatContext);
    const { externals: liveWebExternals } = useLiveWebExternals(
      chatCtx?.dialogueId,
    );

    // Loaded-history fallback: a reopened dialogue's messages land in
    // ChatStore.dialogue.
    const storeMessages = useChatStore((s) => s.dialogue);
    const clientWebExternals = useMemo(() => {
      const map = new Map(liveWebExternals);
      for (const [id, ext] of collectWebExternalsFromMessages(storeMessages)) {
        if (!map.has(id)) map.set(id, ext);
      }
      return map;
    }, [liveWebExternals, storeMessages]);

    const { citationSources, citationNumbers } = useMemo(() => {
      const globalCited = dialogueProvenance?.assistant_response_sources ?? [];
      const unavailableCited = (
        dialogueProvenance?.unavailable_sources ?? []
      ).filter((u) => u.cited);
      // Cited EXTERNALS also occupy dialogue-global slots. A web-only answer
      // cites no chunks at all, so bailing out on the chunk tiers alone left
      // its externals unfolded and every marker unnumbered (#20423 A2).
      const citedExternals = (
        dialogueProvenance?.external_sources ?? []
      ).filter((e) => e.cited);
      // Externals cited in THIS answer that we can already resolve client-side.
      const clientCitedIds = citedExternalIdsInOrder(
        content,
        clientWebExternals,
      );
      const hasProvenance =
        globalCited.length > 0 ||
        unavailableCited.length > 0 ||
        citedExternals.length > 0;
      if (!hasProvenance && clientCitedIds.length === 0) {
        // No dialogue payload yet (or a legacy backend) and nothing to resolve
        // client-side: index-based numbering over the per-message sources.
        return {
          citationSources: mergedResponseSources,
          citationNumbers: undefined,
        };
      }
      const merged = mergeCitedWithUnavailable(globalCited, unavailableCited);
      const sources: any[] = [];
      const numbers = new Map<number, number>();
      const seen = new Set<number>();
      let maxNumber = 0;
      for (const entry of merged) {
        if (entry.citationNumber != null) {
          maxNumber = Math.max(maxNumber, entry.citationNumber);
        }
        if (entry.kind !== "source") {
          // A cited-but-unavailable document keeps its stable slot so the
          // inline marker matches the panel's "Document unavailable" card (D4).
          const chunkId = entry.entry.chunk_id;
          if (chunkId != null && entry.citationNumber != null) {
            numbers.set(chunkId, entry.citationNumber);
            seen.add(chunkId);
          }
          continue;
        }
        sources.push(entry.source);
        const chunkId = entry.source.data_chunk?.id;
        if (chunkId != null) {
          numbers.set(chunkId, entry.citationNumber);
          seen.add(chunkId);
        }
      }
      // Cited externals own backend-assigned slots too, so reserve them before
      // handing out provisional numbers below — otherwise a still-finalizing
      // turn could hand [1] to a chunk that an external already holds.
      for (const ext of citedExternals) {
        if (ext.citation_index != null) {
          maxNumber = Math.max(maxNumber, ext.citation_index);
        }
      }
      for (const extra of mergedResponseSources) {
        const chunkId = extra.data_chunk?.id;
        if (chunkId == null || seen.has(chunkId)) continue;
        seen.add(chunkId);
        sources.push(extra);
        maxNumber += 1;
        numbers.set(chunkId, maxNumber);
      }
      // Cited EXTERNAL sources (ADAMS/OSTI/web) are numbered by the backend in
      // the same sequence (citation_index) and keyed by their synthetic cite_id
      // — fold them into the inline registry so <cite>{cite_id}</cite> resolves
      // and renders its [N] like a chunk citation (#20423 A2). Shaped so rehype
      // recognizes them as external (no data_chunk).
      for (const ext of dialogueProvenance?.external_sources ?? []) {
        if (!ext.cited || ext.cite_id == null || ext.citation_index == null) {
          continue;
        }
        if (seen.has(ext.cite_id)) continue;
        seen.add(ext.cite_id);
        numbers.set(ext.cite_id, ext.citation_index);
        maxNumber = Math.max(maxNumber, ext.citation_index);
        sources.push({ __external: true, cite_id: ext.cite_id, external: ext });
      }
      // Client-resolved web externals fill any cite the backend provenance
      // hasn't delivered yet — the live path that makes a streaming web answer
      // number its cites without waiting on persistence (#20423 A2). Numbered by
      // first-cited order; skipped once dialogueProvenance owns the same id, so
      // the authoritative backend number wins the moment it loads.
      for (const id of clientCitedIds) {
        if (seen.has(id) || numbers.has(id)) continue;
        seen.add(id);
        maxNumber += 1;
        numbers.set(id, maxNumber);
        sources.push({
          __external: true,
          cite_id: id,
          external: clientWebExternals.get(id),
        });
      }
      return { citationSources: sources, citationNumbers: numbers };
    }, [
      dialogueProvenance,
      mergedResponseSources,
      content,
      clientWebExternals,
    ]);

    // Update parent when sources change
    useEffect(() => {
      if (onSourcesUpdate && fetchedChunks && fetchedChunks.length > 0) {
        const hasNewSources = fetchedChunks.some(
          (chunk) =>
            !responseSources.some(
              (source) => source.data_chunk?.id === chunk.data_chunk?.id,
            ),
        );

        if (hasNewSources) {
          // Removed noisy debug log: "🔍 AAAssistantText: Updating parent with merged sources"
          onSourcesUpdate(mergedResponseSources);
        }
      }
    }, [
      mergedResponseSources,
      onSourcesUpdate,
      fetchedChunks,
      responseSources,
    ]);

    // Allow disabling rehype parsing via UI store flag
    const { preferences } = useUIStore();
    const disableRehype = preferences.disableRehype;

    const processedContent = useMemo(
      () => protectCitationMarkup(content),
      [content],
    );

    // Plugin arrays and the components map MUST be referentially stable
    // across re-renders. A fresh `cite` renderer identity makes React treat
    // every citation as a new component type and remount it; the remounted
    // CitationLink re-registers its chunk id, useCitationSources notifies,
    // state updates, and the cycle repeats — a self-sustaining remount loop
    // that tore the citation DOM down every ~150ms and made the hover
    // tooltip flicker (#20423).
    const rehypePlugins: Pluggable[] = useMemo(
      () =>
        disableRehype
          ? []
          : [
              rehypeRaw,
              [
                rehypeWrapCite,
                { responseSources: citationSources, citationNumbers },
              ],
              rehypeWrapStorageKey,
              rehypeKatex,
            ],
      [disableRehype, citationSources, citationNumbers],
    );

    const remarkPlugins: Pluggable[] = useMemo(
      () => (disableRehype ? [] : [remarkGfm, remarkMath]),
      [disableRehype],
    );

    const component: Partial<Components> = useMemo(
      () => ({
        ...makeCitationComponents({
          citationsLoading,
          chunkMap,
          messageId: chatMessage?.id,
        }),
        code: CodeComponent,
        a: (props: any) => <a {...props} target="_blank" />,
      }),
      [chunkMap, citationsLoading, chatMessage?.id],
    );

    // Check if content is a workflow artifact result with error handling
    let workflowResult = null;

    try {
      // Simple check for artifact result pattern
      if (content && typeof content === "string") {
        const parsed = JSON.parse(content);

        // Check if this is a direct artifact result
        if (
          typeof parsed === "object" &&
          parsed !== null &&
          "artifact_id" in parsed &&
          "message" in parsed
        ) {
          workflowResult = parsed;
        }
        // Check if this is a nested task result with artifact in the result field
        else if (
          typeof parsed === "object" &&
          parsed !== null &&
          "result" in parsed &&
          typeof parsed.result === "string"
        ) {
          try {
            const nestedResult = JSON.parse(parsed.result);
            if (
              typeof nestedResult === "object" &&
              nestedResult !== null &&
              "artifact_id" in nestedResult &&
              "message" in nestedResult
            ) {
              workflowResult = nestedResult;
            }
          } catch (nestedError) {
            // Nested result is not valid JSON, continue with normal rendering
          }
        }
      }
    } catch (error) {
      // Not JSON, continue with normal rendering
    }

    // If this is a workflow artifact result, display the artifact
    if (workflowResult) {
      // Add the artifact to the store so it shows up in the RESULT tab
      try {
        addArtifact({
          id: String(workflowResult.artifact_id),
          messageId: chatMessage?.id || "",
          content: { text: "Loading..." },
          isLoading: true,
          type: "markdown",
          parentId: chatMessage?.parent_id || "",
        });
      } catch (error) {
        console.error("Failed to add artifact to store:", error);
      }

      // Dynamic import to avoid formatter issues
      const ArtifactDisplay = require("./Artifacts/ArtifactDisplay").default;

      return (
        <Box
          fontSize="16px"
          className="text-fade-in"
          data-testid="aa-message-streaming-text"
          sx={{
            whiteSpace: "normal",
            wordWrap: "break-word",
            "--animation-duration": "300ms",
            "--fade-animation-function": "linear",
          }}
        >
          <ArtifactDisplay
            artifactId={workflowResult.artifact_id}
            artifactName={workflowResult.artifact_name}
            message={workflowResult.message}
          />
        </Box>
      );
    }

    if (disableRehype) {
      // Render raw text without markdown/rehype parsing
      return (
        <Box
          fontSize="16px"
          className="text-fade-in"
          data-testid="aa-message-streaming-text"
          sx={{
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
            "--animation-duration": "300ms",
            "--fade-animation-function": "linear",
          }}
        >
          <SubAgentContributionStrip messageId={chatMessage?.id} />
          {content}
          <ResponseSourcesSummary messageId={chatMessage?.id} />
        </Box>
      );
    }

    return (
      <Box
        fontSize="16px"
        className="text-fade-in"
        data-testid="aa-message-streaming-text"
        sx={{
          whiteSpace: "normal",
          wordWrap: "break-word",
          "--animation-duration": "300ms",
          "--fade-animation-function": "linear",
        }}
      >
        {/* Sub-agent attribution strip (#20423 WS6): contributor chips, or the
            untraced-sub-agent notice when degradation reports one. */}
        <SubAgentContributionStrip messageId={chatMessage?.id} />
        <Markdown
          remarkPlugins={remarkPlugins}
          rehypePlugins={rehypePlugins}
          components={component}
        >
          {processedContent}
        </Markdown>
        {/* Three-tier provenance summary (Searched → Read → Cited). */}
        <ResponseSourcesSummary messageId={chatMessage?.id} />
      </Box>
    );
  },
);

export default AAAssistantText;
